import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit/src/Palette';
import { Icon } from '@gitbook/spine-icons';
import {
    AlertDialog,
    Button,
    clipboard,
    Description,
    DialogActionButton,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    LoadingIndicator,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useHistoryLocation } from '../../navigation/history';
import { useCurrentOrganizationId, useLoggedInUser } from '../../state';
import { useSpace } from '../Space/useSpace';
import { CommentResolveButton } from './CommentResolveButton';
import { AddEmojiReactionButton } from './Emoji/CommentEmojis';
import { useCommentActions } from './useCommentActions';

/**
 * Mutations for a comment or comment reply. Includes:
 * - resolve and unresolve
 * - add reaction
 * - delete comment (under palette)
 * - edit comment (under palette)
 * - copy URL to comment (under palette)
 */
export function CommentMutations(props: {
    comment: api.Comment;
    commentReply?: api.CommentReply;
    isReply?: boolean;
    onStartEdits: () => void;
    onCancelEdits: () => void;
    onSaveEdits: () => void;
    editing: boolean;
    loadingEdit: boolean;
}): React.ReactElement | null {
    const {
        comment,
        commentReply,
        isReply,
        onStartEdits,
        onCancelEdits,
        onSaveEdits,
        editing,
        loadingEdit,
    } = props;
    const user = useLoggedInUser();
    const resolvedSpaceId = comment.target.space;
    const organizationId = useCurrentOrganizationId();
    assertIsDefined(organizationId);
    const space = useSpace({ organizationId, spaceId: resolvedSpaceId });
    const trigger = useDialogTriggerState();

    const commentActions = useCommentActions({ comment, commentReply });
    const location = useHistoryLocation();

    const canEditOrDelete =
        Boolean(space?.permissions.edit) &&
        user.uid === (isReply ? commentReply?.postedBy.id : comment.postedBy.id);

    /* This component can get rendered in different comment-views, but we want to link to the comment view so we ensure the url is correct. */
    const commentURL = React.useMemo(() => {
        if (location.pathname.includes(comment.id)) {
            // We're already in the view we want to link to (.../~/comments/<comment.id>)
            return new URL(`${location.pathname}`, window.origin).toString();
        }

        // We're not in the view we want to link to, so we add `comment.id` to the url
        return new URL(`${location.pathname}/${comment.id}`, window.origin).toString();
    }, [location.pathname, comment.id]);

    return (
        <DialogRoot {...trigger.rootProps}>
            <div className="absolute top-0 right-0 z-10 flex items-center rounded-lg border border-muted border-solid bg-base p-[1px] opacity-0 shadow-subtle transition-opacity group-focus-within/message:opacity-100 group-hover/message:opacity-100">
                {canEditOrDelete && editing ? (
                    <div className="flex flex-row gap-1">
                        <Button
                            label="Cancel"
                            iconOnly
                            kind="blank"
                            size="small"
                            onPress={onCancelEdits}
                            leadingIcon={Icon.X}
                        />
                        <Button
                            onPress={onSaveEdits}
                            leadingIcon={
                                loadingEdit ? <LoadingIndicator size="xsmall" /> : Icon.Check
                            }
                            size="small"
                            label="Save"
                            tooltip={{ label: 'Save', shortcut: 'Enter' }}
                            iconOnly
                            kind="blank"
                            disabled={loadingEdit}
                        />
                    </div>
                ) : (
                    <>
                        {/* Copy URL to comment */}
                        <Button
                            label="Copy URL to comment"
                            iconOnly
                            kind="blank"
                            size="small"
                            leadingIcon={Icon.Link}
                            onPress={() => {
                                clipboard.writeText(commentURL, {
                                    notify: {
                                        title: 'Copied URL to comment to clipboard',
                                    },
                                });
                            }}
                        />
                        {/* Add emoji reaction */}
                        <AddEmojiReactionButton
                            kind="blank"
                            onReactionAdded={commentActions.onReact}
                        />
                        {/* Resolve/unresolve button */}
                        {isReply ? null : (
                            <CommentResolveButton comment={comment} commentReply={commentReply} />
                        )}

                        {/* More options palette (delete, edit) */}
                        {canEditOrDelete ? (
                            <PaletteButton
                                label="More options"
                                size="small"
                                iconOnly
                                kind="blank"
                                trailingIcon={Icon.MoreVertical}
                                palette={() =>
                                    createPalette({
                                        groups: [
                                            {
                                                key: 'more-options',
                                                commands: [
                                                    {
                                                        key: 'edit',
                                                        title: 'Edit',
                                                        icon: Icon.Pencil,
                                                        trigger: () => {
                                                            onStartEdits();
                                                        },
                                                    },
                                                    {
                                                        key: 'delete',
                                                        title: 'Delete',
                                                        icon: Icon.Trashcan,
                                                        trigger: () => {
                                                            trigger.open();
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    })
                                }
                            />
                        ) : null}
                    </>
                )}
            </div>
            <DeleteCommentDialog trigger={trigger} onDelete={commentActions.onDelete} />
        </DialogRoot>
    );
}

function DeleteCommentDialog(props: {
    trigger: ReturnType<typeof useDialogTriggerState>;
    onDelete: () => Promise<void>;
}): React.ReactElement {
    const { trigger, onDelete } = props;

    return (
        <AlertDialog {...trigger.rootProps}>
            <DialogHeader>
                <Heading>Delete comment?</Heading>
                <Description>
                    You are deleting a comment, and this cannot be undone. Would you like to
                    continue?
                </Description>
            </DialogHeader>
            <DialogFooter>
                <DialogCloseButton />
                <DialogActionButton kind="danger" label="Delete" onPress={onDelete} />
            </DialogFooter>
        </AlertDialog>
    );
}
